import { toast } from 'sonner';
import { confirmAction } from '@/components/dialogs/confirm-action';
import { getLoanMutationErrorMessage, isForbiddenError } from '@/services/api/loans';

export interface AuditedLoanMutationFlow {
  title: string;
  description: string;
  actionLabel?: string;
  destructive?: boolean;
  /** Toast fallback when the failure body carries no `{ error }` */
  fallbackError: string;
  execute: (comment: string) => Promise<unknown>;
}

export type LoanMutationOutcome = 'cancelled' | 'unauthorised' | 'failed' | 'success';

/**
 * Shared audit-dialog flow for every loan mutation: confirm with a
 * justification, then map failures — HTTP 403 resolves as 'unauthorised' so the
 * caller opens the Unauthorised Action dialog; any other failure is
 * toasted with the server `{ error }` (or the flow's fallback) and resolves as
 * 'failed' so the invoking dialog stays open for a retry.
 */
export const runAuditedLoanMutation = async ({ title, description, actionLabel, destructive, fallbackError, execute }: AuditedLoanMutationFlow): Promise<LoanMutationOutcome> => {
  let isForbidden = false;
  let failureMessage: string | null = null;
  const justification = await confirmAction({
    title,
    description,
    actionLabel,
    destructive,
    onConfirm: async (comment) => {
      try {
        await execute(comment);
      } catch (error) {
        // Swallow rather than rethrow: throwing keeps the confirm dialog open, but the
        // spec routes these failures to the unauthorised dialog / a toast instead.
        if (isForbiddenError(error)) {
          isForbidden = true;
          return;
        }
        failureMessage = getLoanMutationErrorMessage(error, fallbackError);
      }
    },
  });
  if (justification === null) return 'cancelled';
  if (isForbidden) return 'unauthorised';
  if (failureMessage !== null) {
    toast.error(failureMessage);
    return 'failed';
  }
  return 'success';
};
